import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { toast } from 'react-toastify';
import http from '../../services/http';
import {
  LIMITE_DA_SAUDACAO,
  LIMITE_DE_DEPARTAMENTOS,
  LIMITE_DO_TITULO,
  MENSAGEM_SEM_PERMISSAO,
  VARIAVEIS_DA_MENSAGEM,
  OPCOES_DE_AGRUPAMENTO,
  avisosDoMenu,
  corpoDoErro,
  corpoDoFormulario,
  departamentoAtivo,
  errosDaApi,
  errosDoFormulario,
  formularioVazio,
  idsDosDepartamentos,
  mensagemDoErro,
  moverDepartamento,
  rotuloDoDepartamento,
  statusDoErro,
  textoDaCredencial,
  tomLegivel,
} from '../../utils/numeroDeWhatsapp';

/**
 * A tela de UM número de WhatsApp: cadastro e edição.
 *
 * ## Por que página, e não o diálogo de antes
 *
 * O formulário tem 390 linhas de campo — identidade na Meta, saudação,
 * agrupamento, menu de departamentos, token, segredo do app próprio e o
 * estado do número. Tudo isso vivia empilhado num diálogo `maxWidth="sm"`,
 * numa rolagem só dentro de uma caixa.
 *
 * Em página cabe, e as abas separam o que são tarefas diferentes: mexer na
 * saudação não é a mesma coisa que trocar uma credencial.
 *
 * ## Duas abas, e não três
 *
 * O desenho inicial previa uma de "Webhook". Ela ficaria quase vazia: a URL
 * exclusiva do app próprio aparece no CARTÃO da lista, não no formulário.
 * Aba vazia para casar com o desenho é pior que duas que significam algo.
 *
 * "Número ativo" ficou em Configuração: é um ajuste do número, não uma
 * credencial.
 */
const WhatsAppNumeroPage = () => {
  const history = useHistory();
  const { id } = useParams();
  const edicao = id !== 'novo';

  const [aba, setAba] = useState('configuracao');
  const [carregando, setCarregando] = useState(true);
  const [semPermissao, setSemPermissao] = useState(false);
  const [erroDaCarga, setErroDaCarga] = useState('');

  const [form, setForm] = useState(formularioVazio);
  const [emEdicao, setEmEdicao] = useState(null);
  const [departamentos, setDepartamentos] = useState([]);
  const [erroDosDepartamentos, setErroDosDepartamentos] = useState(false);

  const [errosPorCampo, setErrosPorCampo] = useState({});
  const [erroDoSalvamento, setErroDoSalvamento] = useState('');
  const [salvando, setSalvando] = useState(false);

  // Os campos de credencial nascem mascarados. O valor GUARDADO nunca aparece
  // (a API não o devolve), mas o DIGITADO ficava legível na tela e no atributo
  // `value` do DOM -- num painel de atendimento, onde a tela é compartilhada e
  // tem gente ao lado. O botão de revelar existe porque quem cola um token
  // longo precisa conferir o que colou.
  const [mostrarToken, setMostrarToken] = useState(false);
  const [mostrarSegredo, setMostrarSegredo] = useState(false);
  // O GESTO explícito de trocar a credencial, na edição. Enquanto é `false` o
  // campo nasce desabilitado e a chave nem existe no corpo do `PUT` -- ver
  // `corpoDoFormulario`. É a defesa que não depende de o navegador respeitar o
  // `autoComplete`, e gerenciador de senha de terceiro não respeita.
  const [trocarToken, setTrocarToken] = useState(false);
  const [trocarSegredo, setTrocarSegredo] = useState(false);

  const carregar = useCallback(async () => {
    setErroDaCarga('');
    setSemPermissao(false);
    setErroDosDepartamentos(false);

    try {
      // Os departamentos vêm com `catch` próprio: sem eles a tela ainda serve
      // (dá para salvar o número e montar o menu depois), e derrubar o
      // formulário inteiro por causa do seletor seria trocar um problema
      // pequeno por um grande. Mas a falha é REGISTRADA.
      const [numero, grupos] = await Promise.all([
        edicao ? http.get(`/whatsapp-numbers/${id}`) : Promise.resolve(null),
        http.get('/groups').catch(() => {
          setErroDosDepartamentos(true);
          return { data: [] };
        }),
      ]);

      setDepartamentos((Array.isArray(grupos?.data) ? grupos.data : []).filter(departamentoAtivo));

      if (edicao) {
        const dados = numero?.data?.data || numero?.data || null;

        if (!dados || dados.id == null) {
          setErroDaCarga('Número não encontrado.');
          return;
        }

        setEmEdicao(dados);
        setForm({
          phone_number_id: dados.phone_number_id || '',
          waba_id: dados.waba_id || '',
          display_number: dados.display_number || '',
          rotulo: dados.rotulo || '',
          saudacao: dados.saudacao || '',
          mensagem_pos_departamento: dados.mensagem_pos_departamento || '',
          mensagem_de_encerramento: dados.mensagem_de_encerramento || '',
          agrupar_por_segundos: Number(dados.agrupar_por_segundos) || 0,
          // Credenciais SEMPRE em branco: a API não devolve o valor, e em
          // branco significa "mantenha o que está guardado".
          token: '',
          app_secret: '',
          usa_app_proprio: Boolean(dados.usa_app_proprio),
          ativo: Boolean(dados.ativo),
          departamentos: idsDosDepartamentos(dados),
        });
      }
    } catch (e) {
      if (statusDoErro(e) === 403) {
        setSemPermissao(true);
      } else {
        setErroDaCarga(mensagemDoErro(e, 'Não foi possível carregar este número.'));
      }
    } finally {
      setCarregando(false);
    }
  }, [edicao, id]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  // A marca de erro some ao corrigir o campo. Mantê-la até o próximo Salvar faz
  // a tela continuar acusando um problema que a pessoa acabou de resolver -- e
  // aí ela para de confiar na marca.
  const limparErroDoCampo = (campo) => {
    setErrosPorCampo((anterior) => {
      if (anterior[campo] === undefined) return anterior;
      const restante = { ...anterior };
      delete restante[campo];
      return restante;
    });
  };

  const mudar = (campo) => (evento) => {
    const valor = evento?.target?.type === 'checkbox' ? evento.target.checked : evento.target.value;
    setForm((anterior) => ({ ...anterior, [campo]: valor }));
    limparErroDoCampo(campo);
  };

  const adicionarDepartamento = (evento) => {
    const idGrupo = Number(evento.target.value);
    if (!Number.isInteger(idGrupo)) return;
    setForm((anterior) => {
      if (anterior.departamentos.includes(idGrupo)) return anterior;
      if (anterior.departamentos.length >= LIMITE_DE_DEPARTAMENTOS) return anterior;
      return { ...anterior, departamentos: [...anterior.departamentos, idGrupo] };
    });
    limparErroDoCampo('departamentos');
  };

  const removerDepartamento = (idGrupo) => {
    setForm((anterior) => ({
      ...anterior,
      departamentos: anterior.departamentos.filter((x) => x !== idGrupo),
    }));
    // O menu não passa pelo `mudar()` (não é campo de texto), então sem isto a
    // queixa fica na tela DEPOIS de a pessoa resolver o problema. Marca que
    // mente é marca em que ninguém confia -- e eu perdi esta linha ao mover o
    // formulário para cá; foi o teste que a trouxe de volta.
    limparErroDoCampo('departamentos');
  };

  const mover = (de, para) => {
    setForm((anterior) => ({
      ...anterior,
      departamentos: moverDepartamento(anterior.departamentos, de, para),
    }));
  };

  // O que ainda não está no menu. Sem este filtro, o seletor ofereceria um
  // departamento que já está na lista, e escolhê-lo não faria nada visível.
  const departamentosDisponiveis = departamentos.filter(
    (grupo) => !form.departamentos.includes(Number(grupo?.id)),
  );

  // Devolve o MAPA, e não uma função: o JSX abaixo faz `.get(id)`. Escrevi
  // como função na primeira versão e o teste pegou -- `.get is not a function`.
  const nomeDoDepartamento = useMemo(() => {
    const mapa = new Map();
    departamentos.forEach((grupo) => {
      if (grupo?.id != null) mapa.set(Number(grupo.id), rotuloDoDepartamento(grupo));
    });
    return mapa;
  }, [departamentos]);

  // Os avisos do menu são calculados no RENDER, e não no `salvar()`: é antes de
  // salvar que eles servem. Quem descobre depois já mandou o menu cortado para a
  // Meta e vai descobrir de novo pela reclamação de quem recebeu.
  const avisosDoMenuAtual = avisosDoMenu(form.departamentos, departamentos);

  const voltar = () => history.push('/whatsapp');

  const salvar = async () => {
    const tinhaAppProprio = Boolean(emEdicao?.usa_app_proprio);
    const erros = errosDoFormulario(form, { edicao, tinhaAppProprio, trocarToken, trocarSegredo });

    setErrosPorCampo(erros);
    if (Object.keys(erros).length > 0) {
      setErroDoSalvamento('Confira os campos destacados.');
      // Leva para a aba do campo com problema. Sem isto, a marca vermelha
      // pode estar na OUTRA aba e a pessoa lê "confira os campos destacados"
      // olhando uma tela sem destaque nenhum.
      const soDeCredencial = Object.keys(erros)
        .every((campo) => ['token', 'app_secret'].includes(campo));
      setAba(soDeCredencial ? 'credenciais' : 'configuracao');
      return;
    }

    setSalvando(true);
    setErroDoSalvamento('');
    const corpo = corpoDoFormulario(form, { edicao, trocarToken, trocarSegredo });
    try {
      if (edicao) {
        await http.put(`/whatsapp-numbers/${emEdicao.id}`, corpo);
      } else {
        await http.post('/whatsapp-numbers', corpo);
      }
      toast.success(edicao ? 'Número atualizado.' : 'Número cadastrado.');
      voltar();
    } catch (e) {
      setErroDoSalvamento(mensagemDoErro(e, 'Não foi possível salvar o número. Tente novamente.'));
      // As CHAVES do 422 dizem qual campo o servidor recusou. Sem isto a queixa
      // aparecia no alerta e o campo culpado ficava sem marca nenhuma -- e o
      // único 422 que a tela não consegue prever (phone_number_id duplicado) é
      // justamente o de um campo que parece certo a olho nu.
      const daApi = errosDaApi(corpoDoErro(e));
      if (Object.keys(daApi).length > 0) setErrosPorCampo(daApi);
    } finally {
      setSalvando(false);
    }
  };

  if (semPermissao) {
    return (
      <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: '100%' }}>
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <LockOutlinedIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
          <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>Acesso não liberado</Typography>
          <Typography variant="body2" color="text.secondary">{MENSAGEM_SEM_PERMISSAO}</Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Tooltip title="Voltar para a lista">
          <IconButton onClick={voltar} aria-label="Voltar para a lista de números">
            <ArrowBackIcon />
          </IconButton>
        </Tooltip>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {edicao ? (emEdicao?.rotulo || 'Número de WhatsApp') : 'Novo número de WhatsApp'}
        </Typography>
      </Box>

      {carregando && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {!carregando && erroDaCarga && <Alert severity="error">{erroDaCarga}</Alert>}

      {/*
        Sem largura fixa: a tela acompanha a janela, igual à lista de números.

        Cheguei a limitar o formulário a 880 e depois a 1400, pela regra de que
        campo de texto muito largo fica ilegível. Na prática ficou pior: uma
        tira estreita no meio de uma tela de 5000px, justamente o que a
        mudança tinha ido consertar. Quem legibiliza aqui é a GRADE abaixo --
        os campos dividem a largura em vez de um só esticar por ela.
      */}
      {!carregando && !erroDaCarga && (
        <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 } }}>
          <Tabs
            value={aba}
            onChange={(e, nova) => setAba(nova)}
            sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab value="configuracao" label="Configuração" />
            <Tab value="credenciais" label="Credenciais" />
          </Tabs>

          {/* O erro do salvamento fica FORA das abas: ele fala do formulário
              inteiro, e dentro de uma aba sumiria justamente quando a pessoa
              troca para a outra procurando o que deu errado. */}
          {erroDoSalvamento && <Alert severity="error" sx={{ mb: 2 }}>{erroDoSalvamento}</Alert>}

          {/* `display: none` em vez de desmontar: trocar de aba não pode
              perder o que já foi digitado na outra. */}
          <Box sx={{ display: aba === 'configuracao' ? 'block' : 'none' }}>

          {/*
            Os quatro campos de identidade numa grade que ACOMPANHA a tela, em
            vez de largura fixa. A página inteira ocupa a largura, como a lista
            de números; limitar o formulário a 1400 deixava uma tira no meio do
            vazio -- pior que o problema que a mudança foi consertar.

            `auto-fit` com quatro itens é o que faz isso sozinho: as faixas que
            sobram COLAPSAM, então nunca passa de quatro colunas por mais larga
            que a tela seja. Numa tela estreita vira uma, em 1000px vira três,
            e num monitor largo os quatro ficam na mesma linha. O
            `minmax(280px, 1fr)` é o que impede a coluna de ficar menor que o
            rótulo.
          */}
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            columnGap: 2,
          }}>
          <TextField
            label="Rótulo"
            placeholder="Ex.: SysFar"
            fullWidth
            value={form.rotulo}
            onChange={mudar('rotulo')}
            error={Boolean(errosPorCampo.rotulo)}
            helperText={errosPorCampo.rotulo || 'Como este número aparece aqui no painel.'}
            sx={{ mb: 2 }}
            autoFocus
          />
          <TextField
            label="Número como o cliente o vê"
            placeholder="Ex.: +55 19 99999-0000"
            fullWidth
            value={form.display_number}
            onChange={mudar('display_number')}
            error={Boolean(errosPorCampo.display_number)}
            helperText={errosPorCampo.display_number || ' '}
            sx={{ mb: 2 }}
          />
          <TextField
            label="ID do número na Meta (phone_number_id)"
            fullWidth
            value={form.phone_number_id}
            onChange={mudar('phone_number_id')}
            error={Boolean(errosPorCampo.phone_number_id)}
            helperText={errosPorCampo.phone_number_id || ' '}
            sx={{ mb: 2 }}
          />
          <TextField
            label="ID da conta do WhatsApp Business (waba_id)"
            fullWidth
            value={form.waba_id}
            onChange={mudar('waba_id')}
            error={Boolean(errosPorCampo.waba_id)}
            helperText={errosPorCampo.waba_id || ' '}
            sx={{ mb: 2 }}
          />
          </Box>

          {/* A saudação fica FORA da grade: é texto de várias linhas, e ao
              lado de um campo de uma linha a caixa dela ficaria desalinhada
              com tudo. */}
          <TextField
            label="Saudação"
            fullWidth
            multiline
            minRows={2}
            value={form.saudacao}
            onChange={mudar('saudacao')}
            error={Boolean(errosPorCampo.saudacao)}
            helperText={
              errosPorCampo.saudacao
              || `Texto que abre a conversa, antes do menu. Em branco, usa o padrão. Até ${LIMITE_DA_SAUDACAO} caracteres.`
            }
            sx={{ mb: 2 }}
          />

          {/*
            As outras duas falas da instância. A saudação acima abre a
            conversa; estas duas fecham os outros dois momentos em que o chat
            do site já falava com o cliente e o WhatsApp não.

            Em branco significa NÃO ENVIAR -- e o texto de ajuda diz isso com
            todas as letras, porque é o oposto da saudação (que em branco usa
            um padrão). Cada mensagem no WhatsApp é cobrada pela Meta, e
            inventar uma frase que ninguém escreveu gastaria o dinheiro do
            cliente por conta própria.
          */}
          <TextField
            label="Mensagem após escolher o departamento"
            fullWidth
            multiline
            minRows={2}
            value={form.mensagem_pos_departamento}
            onChange={mudar('mensagem_pos_departamento')}
            error={Boolean(errosPorCampo.mensagem_pos_departamento)}
            helperText={
              errosPorCampo.mensagem_pos_departamento
              || 'Enviada assim que o cliente escolhe (ou o número tem um departamento só). Em branco, não envia nada.'
            }
            sx={{ mb: 2 }}
          />

          <TextField
            label="Mensagem de encerramento"
            fullWidth
            multiline
            minRows={2}
            value={form.mensagem_de_encerramento}
            onChange={mudar('mensagem_de_encerramento')}
            error={Boolean(errosPorCampo.mensagem_de_encerramento)}
            helperText={
              errosPorCampo.mensagem_de_encerramento
              || 'Enviada quando o atendimento é encerrado, antes do pedido de avaliação. Em branco, não envia nada.'
            }
            sx={{ mb: 1 }}
          />

          {/*
            A lista de variáveis fica AQUI, colada nos campos que as usam.

            Marcador desconhecido é enviado como está -- quem escrever
            `{departamanto}` verá a palavra torta chegar no celular do
            cliente. Esta lista é o único lugar onde dá para perceber antes.
          */}
          {/* As cores saem do tema pela forma de callback do `sx`: esta página
              não tem o tema em escopo, e assim ela acompanha o claro e o
              escuro sem ninguém precisar lembrar de conferir os dois. */}
          <Box
            sx={(tema) => ({
              mb: 2,
              p: 1.5,
              borderRadius: 1.5,
              border: `1px solid ${tema.palette.divider}`,
              backgroundColor: tema.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.04)'
                : 'rgba(0, 0, 0, 0.03)',
            })}
          >
            <Typography
              variant="caption"
              sx={(tema) => ({ fontWeight: 600, color: tema.palette.text.primary })}
            >
              Variáveis que você pode usar nos dois textos acima
            </Typography>
            {VARIAVEIS_DA_MENSAGEM.map((variavel) => (
              <Typography
                key={variavel.nome}
                variant="caption"
                sx={(tema) => ({ display: 'block', color: tema.palette.text.secondary, mt: 0.25 })}
              >
                <code>{`{${variavel.nome}}`}</code> — {variavel.descricao}
              </Typography>
            ))}
            <Typography
              variant="caption"
              sx={(tema) => ({ display: 'block', color: tema.palette.text.secondary, mt: 0.75 })}
            >
              Variável sem valor no momento do envio vira vazio. Nome fora desta lista é enviado como está.
            </Typography>
          </Box>

          {/* Lista de opções, e não campo numérico livre. Um campo livre
              aceitaria "0,5" e "900" e deixaria a recusa para o servidor,
              depois de a pessoa preencher o resto. E as opções soltas aqui
              são as que fazem sentido num atendimento — quem precisa de outra
              está resolvendo outro problema. */}
          <TextField
            select
            label="Agrupar mensagens seguidas"
            fullWidth
            value={form.agrupar_por_segundos}
            onChange={mudar('agrupar_por_segundos')}
            error={Boolean(errosPorCampo.agrupar_por_segundos)}
            helperText={
              errosPorCampo.agrupar_por_segundos
              || 'O operador vê cada mensagem na hora; o cliente recebe as desse intervalo juntas, '
                + 'numa só. A Meta cobra por mensagem, então juntar economiza.'
            }
            inputProps={{ 'data-testid': 'agrupar-por-segundos' }}
            sx={{ mb: 2 }}
          >
            {OPCOES_DE_AGRUPAMENTO.map((opcao) => (
              <MenuItem key={opcao.valor} value={opcao.valor}>{opcao.rotulo}</MenuItem>
            ))}
          </TextField>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Menu de departamentos
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            A ordem desta lista É a numeração do menu que o cliente lê no WhatsApp. O primeiro item
            é a opção 1. No máximo {LIMITE_DE_DEPARTAMENTOS} opções, e o nome de cada uma aparece
            cortado a partir de {LIMITE_DO_TITULO} caracteres — os dois limites são da Meta.
          </Typography>

          {/* Os dois avisos são de severidade diferente de propósito. Passar de
              10 é ERRO (a API recusa, e a Meta recusaria a mensagem inteira);
              nome comprido é AVISO, porque o corte entrega um menu que funciona
              e recusar travaria o mesmo PUT que troca um token vazado. O `Alert`
              do MUI resolve os dois temas sozinho -- cor fixa aqui já mordeu
              este projeto antes. */}
          {errosPorCampo.departamentos && (
            <Alert severity="error" sx={{ mb: 1 }}>{errosPorCampo.departamentos}</Alert>
          )}
          {avisosDoMenuAtual.map((aviso) => (
            <Alert key={aviso} severity="warning" sx={{ mb: 1 }}>{aviso}</Alert>
          ))}

          {form.departamentos.length === 0 ? (
            <Typography variant="body2" sx={{ mb: 1, color: tomLegivel('warning') }}>
              Sem departamentos, a conversa cai na saudação sem nenhuma opção.
            </Typography>
          ) : (
            <Box sx={{ mb: 1 }}>
              {form.departamentos.map((id, indice) => (
                <Box
                  key={id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    // Sem o `flexWrap`, em largura de celular os três botões
                    // comprimiam o nome do departamento até sobrar uma coluna de
                    // letras. Aqui os botões descem para a linha de baixo.
                    flexWrap: 'wrap',
                    gap: 1,
                    py: 0.5,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 20, color: 'text.primary' }}>
                    {indice + 1}.
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ flex: '1 1 8rem', minWidth: '8rem', color: 'text.primary' }}
                  >
                    {nomeDoDepartamento.get(id) || `Departamento ${id}`}
                  </Typography>
                  <Tooltip title="Subir no menu">
                    <span>
                      <IconButton
                        size="small"
                        disabled={indice === 0}
                        onClick={() => mover(indice, indice - 1)}
                        aria-label={`Subir ${nomeDoDepartamento.get(id) || id}`}
                      >
                        <ArrowUpwardIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Descer no menu">
                    <span>
                      <IconButton
                        size="small"
                        disabled={indice === form.departamentos.length - 1}
                        onClick={() => mover(indice, indice + 1)}
                        aria-label={`Descer ${nomeDoDepartamento.get(id) || id}`}
                      >
                        <ArrowDownwardIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Tirar do menu">
                    <IconButton
                      size="small"
                      onClick={() => removerDepartamento(id)}
                      aria-label={`Tirar ${nomeDoDepartamento.get(id) || id} do menu`}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              ))}
            </Box>
          )}

          <Select
            fullWidth
            size="small"
            displayEmpty
            value=""
            onChange={adicionarDepartamento}
            disabled={departamentosDisponiveis.length === 0}
            inputProps={{ 'aria-label': 'Acrescentar departamento ao menu' }}
            sx={{ mb: 2 }}
          >
            <MenuItem value="" disabled>
              {/* Três casos, e não dois: antes a falha do `GET /groups` caía no
                  "todos já estão no menu" -- uma afirmação falsa exatamente
                  quando a lista não pôde ser carregada. */}
              {erroDosDepartamentos
                ? 'Não foi possível carregar os departamentos'
                : departamentosDisponiveis.length === 0
                  ? 'Todos os departamentos ativos já estão no menu'
                  : 'Acrescentar departamento ao fim do menu'}
            </MenuItem>
            {departamentosDisponiveis.map((grupo) => (
              <MenuItem key={grupo.id} value={grupo.id}>
                {rotuloDoDepartamento(grupo)}
              </MenuItem>
            ))}
          </Select>
          <FormControlLabel
            control={<Switch checked={form.ativo} onChange={mudar('ativo')} />}
            label="Número ativo"
          />
          {/* O texto dizia "sai do roteamento de entrada e do envio", e era o
              OPOSTO do que o código faz: `NumeroDaSala` busca o número estampado
              na sala por `id`, sem olhar `ativo`. Quem lesse a frase anterior
              desativaria um número de token vazado e sairia da tela achando que
              a marca estava fora do ar, enquanto os operadores continuavam
              despachando por ele em toda conversa já estampada -- que são todas
              as que aquele número já atendeu. */}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            Desativado, o número para de RECEBER mensagem nova e deixa de ser candidato a responder
            conversa antiga sem origem — e o
            histórico das conversas continua no lugar. O envio NÃO para: as conversas que já
            entraram por este número continuam sendo respondidas por ele, porque a janela de 24h da
            Meta é do par (número, pessoa) e responder por outro número seria falar com o cliente
            de um número que ele nunca contatou. Para tirar a marca do ar agora, revogue o token
            deste número na Meta.
          </Typography>
          </Box>

          <Box sx={{ display: aba === 'credenciais' ? 'block' : 'none' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Credenciais da Meta</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Os valores nunca são exibidos aqui — a API não os devolve, nem em parte.
            {emEdicao && ` Hoje: ${textoDaCredencial(emEdicao)}.`}
          </Typography>

          {emEdicao && !trocarToken && (
            /* O GESTO. Enquanto ele não acontece o campo fica desabilitado e a
               chave nem existe no corpo do `PUT`: nada que uma extensão faça com
               o campo chega ao servidor. O `autoComplete` abaixo continua, e as
               duas defesas são independentes de propósito -- a dica evita o
               preenchimento, o gesto evita o estrago quando a dica for ignorada
               (1Password e LastPass ignoram). */
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
              <Button size="small" variant="outlined" onClick={() => setTrocarToken(true)}>
                Trocar o token
              </Button>
              <Typography variant="caption" color="text.secondary" sx={{ flex: '1 1 12rem' }}>
                O token atual continua guardado. Só é substituído se você pedir a troca aqui.
              </Typography>
            </Box>
          )}

          {(!emEdicao || trocarToken) && (
          <TextField
            label={emEdicao ? 'Token de acesso novo' : 'Token de acesso'}
            fullWidth
            type={mostrarToken ? 'text' : 'password'}
            value={form.token}
            onChange={mudar('token')}
            error={Boolean(errosPorCampo.token)}
            // `new-password` e não `off`: é o valor que os gerenciadores de senha
            // de verdade respeitam. Vale como primeira defesa; a que não depende
            // do navegador é o gesto acima, sem o qual a chave nem entra no
            // corpo da requisição.
            autoComplete="new-password"
            // A API valida `max:4000`; cortar aqui evita um 422 por token colado
            // com sujeira grudada.
            inputProps={{ maxLength: 4000 }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title={mostrarToken ? 'Ocultar' : 'Mostrar o que eu digitei'}>
                    <IconButton
                      size="small"
                      onClick={() => setMostrarToken((v) => !v)}
                      aria-label={mostrarToken ? 'Ocultar o token digitado' : 'Mostrar o token digitado'}
                    >
                      {mostrarToken
                        ? <VisibilityOffOutlinedIcon fontSize="small" />
                        : <VisibilityOutlinedIcon fontSize="small" />}
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
            helperText={
              errosPorCampo.token
              || (emEdicao
                ? 'Este valor substitui o token guardado ao salvar.'
                : 'Token permanente do usuário do sistema, gerado na Meta.')
            }
            sx={{ mb: 2 }}
          />
          )}

          {emEdicao && trocarToken && (
            <Button
              size="small"
              onClick={() => {
                setTrocarToken(false);
                setMostrarToken(false);
                setForm((anterior) => ({ ...anterior, token: '' }));
                setErrosPorCampo((anterior) => {
                  const restante = { ...anterior };
                  delete restante.token;
                  return restante;
                });
              }}
              sx={{ mb: 2, textTransform: 'none' }}
            >
              Cancelar a troca do token
            </Button>
          )}

          <FormControlLabel
            control={<Switch checked={form.usa_app_proprio} onChange={mudar('usa_app_proprio')} />}
            label="Este número usa um app próprio do cliente na Meta"
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Com app próprio, o número ganha um endereço de webhook exclusivo (gerado pelo servidor)
            e as chamadas da Meta são conferidas com o segredo do app do cliente.
            {emEdicao && form.usa_app_proprio === false && emEdicao.usa_app_proprio
              && ' Desligar aqui apaga o segredo guardado e o endereço exclusivo.'}
          </Typography>

          {/* O gesto do segredo só faz sentido quando HÁ segredo guardado. Quem
              está LIGANDO o app próprio agora precisa informá-lo de qualquer
              jeito, então ali o campo aparece direto. */}
          {form.usa_app_proprio && emEdicao?.usa_app_proprio && !trocarSegredo && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
              <Button size="small" variant="outlined" onClick={() => setTrocarSegredo(true)}>
                Trocar o segredo
              </Button>
              <Typography variant="caption" color="text.secondary" sx={{ flex: '1 1 12rem' }}>
                O segredo atual continua guardado. Só é substituído se você pedir a troca aqui.
              </Typography>
            </Box>
          )}

          {form.usa_app_proprio && (!emEdicao?.usa_app_proprio || trocarSegredo) && (
            <TextField
              label={emEdicao?.usa_app_proprio ? 'Segredo do app novo' : 'Segredo do app (app_secret)'}
              fullWidth
              type={mostrarSegredo ? 'text' : 'password'}
              value={form.app_secret}
              onChange={mudar('app_secret')}
              error={Boolean(errosPorCampo.app_secret)}
              autoComplete="new-password"
              inputProps={{ maxLength: 255 }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title={mostrarSegredo ? 'Ocultar' : 'Mostrar o que eu digitei'}>
                      <IconButton
                        size="small"
                        onClick={() => setMostrarSegredo((v) => !v)}
                        aria-label={mostrarSegredo ? 'Ocultar o segredo digitado' : 'Mostrar o segredo digitado'}
                      >
                        {mostrarSegredo
                          ? <VisibilityOffOutlinedIcon fontSize="small" />
                          : <VisibilityOutlinedIcon fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
              helperText={
                errosPorCampo.app_secret
                || (emEdicao?.usa_app_proprio
                  ? 'Este valor substitui o segredo guardado ao salvar.'
                  : 'Obrigatório: sem ele o endereço exclusivo recusaria toda mensagem.')
              }
              sx={{ mb: 2 }}
            />
          )}

          {form.usa_app_proprio && emEdicao?.usa_app_proprio && trocarSegredo && (
            <Button
              size="small"
              onClick={() => {
                setTrocarSegredo(false);
                setMostrarSegredo(false);
                setForm((anterior) => ({ ...anterior, app_secret: '' }));
                setErrosPorCampo((anterior) => {
                  const restante = { ...anterior };
                  delete restante.app_secret;
                  return restante;
                });
              }}
              sx={{ mb: 2, textTransform: 'none' }}
            >
              Cancelar a troca do segredo
            </Button>
          )}
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 3 }}>
            <Button onClick={voltar} disabled={salvando}>Cancelar</Button>
            <Button variant="contained" onClick={salvar} disabled={salvando}>
              {salvando ? 'Salvando...' : 'Salvar'}
            </Button>
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default WhatsAppNumeroPage;
